/**
 * Tells a dead session apart from a genuine "you may not see this page".
 *
 * The permission gates used to funnel every failure into `notAllowed`, so an expired
 * session or a rejected CSRF token rendered "You are not allowed to view this page" while
 * the shell stayed logged in — the SPA authenticates off a localStorage mirror of the JWT
 * (see `setV2Token`), which outlives the server-side session (`isSessionActive` /
 * `currentToken`). The two cases need opposite handling: a dead session must go back to the
 * login screen, a real 403 from PermissionsGuard must stay on the page and say so.
 */
export interface ApiFailure {
  status?: number;
  message?: string;
}

/**
 * True for 401 (expired/replaced session), and for a 403 only when it is the CSRF
 * middleware talking. A plain 403 is PermissionsGuard denying the page — that is a real
 * permission answer and must NOT bounce the user to login.
 */
export function isSessionFailure(failure: ApiFailure | null | undefined): boolean {
  const status = failure?.status;
  if (!status) return false;
  if (status === 401) return true;
  return status === 403 && /csrf/i.test(String(failure?.message ?? ''));
}

/**
 * Drop the stale client-side token and report the dead session.
 *
 * Named "redirect" for history: it no longer navigates. Announcing the fact lets the
 * app-level gate render <NotAuthorized /> in place (see utils/sessionEvents), instead of
 * every caller racing to start its own cross-origin trip to the DCC login — which blanked
 * the page mid-render whenever more than one request 401'd at once.
 */
export async function redirectToLogin(): Promise<void> {
  try {
    const { clearV2Token } = await import('./setV2Token');
    clearV2Token();
  } catch {
    /* best effort — the login flow overwrites the token anyway */
  }
  const { emitSessionExpired } = await import('./sessionEvents');
  emitSessionExpired();
}
